import { CommonModule } from '@angular/common'
import { Component, Input } from '@angular/core'
import { RouterModule } from '@angular/router'
import { Subscription } from 'rxjs/internal/Subscription'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { ContentTypes, Media, Themes } from '../../enums/enums'
import { DdcProfilePicOnlyComponent } from '../ddc-profile-pic-only/ddc-profile-pic-only.component'

@Component({
  selector: 'ddc-notification-content',
  template: `
    <div class="notification-card" [class.unread]="!notification.is_read">
      <div class="notification-card-left">
        <!-- System notification: show lingo logo -->
        <ng-container *ngIf="notification.type === 'system'; else userAvatar">
          <img class="notification-lingo-logo" alt="Lingo AI" [src]="logoSrc" />
        </ng-container>
        <!-- User notification: show actor profile pic, clickable to profile -->
        <ng-template #userAvatar>
          <ddc-profile-pic-only
            *ngIf="notification.actor"
            [dynamicRoute]="'/users/' + notification.actor.username"
            [userProfilePic]="notification.actor.profile_pic"
          ></ddc-profile-pic-only>
          <mat-icon class="notification-default-icon" *ngIf="!notification.actor"
            >account_circle</mat-icon
          >
        </ng-template>
      </div>
      <div class="notification-card-center">
        <div class="notification-card-message">
          <!-- Actor name clickable -->
          <a
            class="notification-actor-link"
            *ngIf="notification.actor?.username"
            [routerLink]="['/users', notification.actor.username]"
            (click)="$event.stopPropagation()"
            >{{ notification.actor.username }}</a
          >
          <span>{{ getActionText(notification) }}</span>
          <!-- Content title clickable if present -->
          <a
            class="notification-content-link"
            *ngIf="notification.content_title && notification.object_id"
            [routerLink]="getContentRoute(notification)"
            (click)="$event.stopPropagation()"
            >{{ notification.content_title }}</a
          >
          <!-- Shared user clickable -->
          <ng-container *ngIf="notification.shared_user">
            <span> from </span>
            <a
              class="notification-actor-link"
              [routerLink]="['/users', notification.shared_user.username]"
              (click)="$event.stopPropagation()"
              >{{ notification.shared_user.username }}</a
            >
          </ng-container>
        </div>
        <div class="notification-card-time">{{ notification.time_since }}</div>
      </div>
      <div class="notification-card-right">
        <mat-icon class="notification-type-icon">{{
          getNotificationIcon(notification.type)
        }}</mat-icon>
      </div>
    </div>
  `,
  styleUrls: ['./notification-content.component.scss'],
  imports: [AngularMaterialsModules, CommonModule, DdcProfilePicOnlyComponent, RouterModule],
})
export class NotificationContentComponent {
  @Input() notification: any
  @Input() type: string
  @Input() currentUsername: string
  @Input() getNotificationIcon!: (type: string) => string
  @Input() getNotificationMessage!: (notification: any) => string

  media = Media.MEDIA

  _isLightTheme: boolean = true
  _isDarkTheme: boolean = false
  _isPaperTheme: boolean = false
  private _themeSubscription: Subscription

  constructor(public themeService: ThemeService) {
    this._themeSubscription = this.themeService.theme$.subscribe(theme => {
      this._isLightTheme = theme === Themes.LIGHT
      this._isDarkTheme = theme === Themes.DARK
      this._isPaperTheme = theme === Themes.PAPER
    })
  }
  get logoSrc(): string {
    if (this._isDarkTheme) {
      return 'assets/media/logos/lingo_logo_dark.png'
    }
    return 'assets/media/logos/lingo_logo_light.png'
  }

  getActionText(notification: any): string {
    switch (notification.type) {
      case 'like':
        return ' liked '
      case 'follow':
        return ' started following you'
      case 'comment':
        return ' commented on '
      case 'share':
        return ' shared '
      case 'system':
        return notification.message || ''
      default:
        return ' '
    }
  }

  getContentRoute(notification: any): string[] {
    const id = notification.object_id?.toString()
    const contentType = notification.content_type
    // For share notifications, the content owner is the shared_user
    // For like/comment notifications, the content belongs to the current user (recipient)
    const owner = notification.shared_user?.username || this.currentUsername

    if (!id || !contentType) return ['/library']

    if (contentType === ContentTypes.DIC) {
      return owner ? ['/library', 'dic', id, owner] : ['/library']
    }

    // For other content types, link to library for now
    return ['/library']
  }
}
